import React, { useState, useRef, useEffect } from 'react';

const AudioPlayer = ({ src, width, height, x, y, onSelect, isSelected, transcript, onDelete, isPlaying: remotePlaying, onPlayPause, currentTime: remoteCurrentTime, onSeek }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Sync seek from remote
  useEffect(() => {
    if (remoteCurrentTime === undefined) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (Math.abs(audio.currentTime - remoteCurrentTime) > 1) {
      audio.currentTime = remoteCurrentTime;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteCurrentTime]);

  // Respond to remote play/pause (from the other participant)
  useEffect(() => {
    if (remotePlaying === undefined) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (remotePlaying && !isPlaying) {
      audio.play().catch(() => {});
    } else if (!remotePlaying && isPlaying) {
      audio.pause();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remotePlaying]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        onPlayPause?.(false);
      } else {
        audioRef.current.play();
        onPlayPause?.(true);
      }
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const newTime = percent * duration;
      audioRef.current.currentTime = newTime;
      onSeek?.(newTime);
    }
  };

  const handleClick = (e) => {
    if (onSelect) {
      onSelect();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const toggleTranscript = () => {
    setShowTranscript(!showTranscript);
  };

  return (
    <foreignObject x={x} y={y} width={width} height={height}>
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          // Explicit px, not '100%' — Safari/WebKit doesn't reliably
          // resolve percentage sizing inside a <foreignObject> that sits
          // under a transformed/scaled ancestor (this whiteboard pans and
          // zooms via the SVG's viewBox on a <g transform="translate(...)">
          // wrapper). The percentage would fail to resolve against the
          // foreignObject's own box in Safari, so the div fell back to
          // its content's natural CSS size and rendered pinned near the
          // document's top-left instead of scaled/positioned with the
          // canvas. Chrome/Firefox never had this bug, which is why it
          // "used to work" for anyone who hadn't tested in Safari yet.
          width: `${width}px`,
          height: `${height}px`,
          position: 'relative',
          cursor: 'pointer',
          background: isSelected 
            ? 'linear-gradient(135deg, rgba(238, 90, 36, 0.1) 0%, rgba(238, 90, 36, 0.05) 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '16px',
          border: isSelected 
            ? '2px solid #ee5a24' 
            : isHovered 
              ? '2px solid rgba(238, 90, 36, 0.3)' 
              : '2px solid #e9ecef',
          overflow: 'visible',
          boxShadow: isSelected 
            ? '0 8px 32px rgba(238, 90, 36, 0.15)' 
            : isHovered 
              ? '0 4px 20px rgba(0, 0, 0, 0.08)' 
              : '0 2px 8px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box'
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >

        <audio
          ref={audioRef}
          src={src}
          controls={false}
          style={{ display: 'none' }}
        />
        
        {/* Audio controls */}
        <div style={{ 
          padding: '16px', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          gap: '12px',
          minHeight: '80px',
          minWidth: '200px',
          transform: 'translateY(-17px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1' }}>
            {/* Play/Pause button */}
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                border: 'none',
                background: isPlaying 
                  ? 'linear-gradient(135deg, #ee5a24 0%, #d94a1a 100%)'
                  : 'linear-gradient(135deg, #ee5a24 0%, #ff6b35 100%)',
                color: '#fff',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isPlaying 
                  ? '0 4px 16px rgba(238, 90, 36, 0.3)' 
                  : '0 2px 8px rgba(238, 90, 36, 0.2)',
                transform: isPlaying ? 'scale(0.95)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = isPlaying ? 'scale(0.9)' : 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(238, 90, 36, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = isPlaying ? 'scale(0.95)' : 'scale(1)';
                e.currentTarget.style.boxShadow = isPlaying 
                  ? '0 4px 16px rgba(238, 90, 36, 0.3)' 
                  : '0 2px 8px rgba(238, 90, 36, 0.2)';
              }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>

            {/* Waveform Progress Track */}
            <div style={{ flex: 1, position: 'relative' }}>
              <div
                onClick={(e) => { e.stopPropagation(); handleSeek(e); }}
                data-seek="true"
                style={{
                  width: '100%',
                  height: '24px',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  padding: '2px 0'
                }}
              >
                {[...Array(40)].map((_, i) => {
                  const progress = (currentTime / duration) * 40;
                  const isPlayed = i < progress;
                  const isCurrent = Math.floor(progress) === i;
                  
                  return (
                    <div
                      key={i}
                      style={{
                        width: '3px',
                        flex: '1',
                        minWidth: '2px',
                        height: `${Math.random() * 15 + 5}px`,
                        background: isPlayed 
                          ? 'linear-gradient(180deg, #ee5a24 0%, #ff6b35 100%)'
                          : isCurrent
                            ? 'linear-gradient(180deg, #ee5a24 0%, #ff6b35 100%)'
                            : '#d1d5db',
                        borderRadius: '1.5px',
                        transition: 'all 0.2s ease-out',
                        animation: isPlaying && isPlayed ? 'pulse 1.5s ease-in-out infinite' : 'none',
                        opacity: isPlayed ? 1 : 0.4,
                        transform: isCurrent ? 'scaleY(1.2)' : 'scaleY(1)',
                        boxShadow: isCurrent ? '0 0 4px rgba(238, 90, 36, 0.5)' : 'none'
                      }}
                    />
                  );
                })}
              </div>
              
              {/* Time display */}
              <div style={{ 
                fontSize: '11px', 
                color: '#5f6368', 
                marginTop: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: '500',
                position: 'relative'
              }}>
                <span style={{ position: 'absolute', left: '0' }}>{formatTime(currentTime)}</span>
                <span style={{ position: 'absolute', right: '0' }}>{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Transcript */}
        {showTranscript && (
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e9ecef',
            background: 'rgba(248, 249, 250, 0.8)',
            borderRadius: '0 0 16px 16px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#495057',
              lineHeight: '1.6',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <strong>Transcript:</strong>
              <br />
              {transcript || 'Transcript is not available for this audio file.'}
            </div>
          </div>
        )}
        
        {/* CSS Animation */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
          `}
        </style>
      </div>
    </foreignObject>
  );
};

export default AudioPlayer; 